import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { UsersFour, X } from "@phosphor-icons/react";
import { api, formatErr } from "@/lib/api";

/**
 * Focused share dialog — used from the Project page header AND from
 * Dashboard project cards. Owner-only. Adds and removes shares.
 *
 * Props:
 *  - project: { project_id, title, shared_users? }
 *  - onClose(): void
 *  - onChanged(partial): called after every successful add/remove with
 *      { shared_users, shared_with } so callers can update local state.
 */
export default function ShareProjectModal({ project, onClose, onChanged }) {
  const [email, setEmail] = useState("");
  const [shared, setShared] = useState(project.shared_users || []);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(!!project.shared_users);

  // Esc to close
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // If we don't already have shared_users (Dashboard cards don't), fetch once.
  const fetchShared = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${project.project_id}`);
      setShared(data.shared_users || []);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoaded(true);
    }
  }, [project.project_id]);

  useEffect(() => {
    if (!loaded) fetchShared();
  }, [loaded, fetchShared]);

  const submit = async (e) => {
    e.preventDefault();
    const target = email.trim().toLowerCase();
    if (!target) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${project.project_id}/share`, { emails: [target] });
      if (data.missing_emails?.length) {
        toast.error(`Not registered on Flowscribe: ${data.missing_emails.join(", ")}`);
      }
      const { data: fresh } = await api.get(`/projects/${project.project_id}`);
      setShared(fresh.shared_users || []);
      onChanged?.({ shared_users: fresh.shared_users, shared_with: fresh.shared_with });
      setEmail("");
      if (!data.missing_emails?.length) toast.success("Project shared");
    } catch (e2) {
      toast.error(formatErr(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (uid) => {
    try {
      await api.delete(`/projects/${project.project_id}/share/${uid}`);
      const next = shared.filter((u) => u.user_id !== uid);
      setShared(next);
      onChanged?.({ shared_users: next, shared_with: next.map((u) => u.user_id) });
      toast.success("Removed");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-[--ink]/60 backdrop-blur-sm grid place-items-center z-50 p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white hard-border max-w-lg w-full p-8 my-8"
        onClick={(e) => e.stopPropagation()}
        data-testid="share-project-modal"
      >
        <div className="flex items-start justify-between mb-2">
          <div>
            <div className="overline text-[--muted]">SHARE</div>
            <h2 className="font-display font-black text-2xl tracking-tighter mt-1 inline-flex items-center gap-2">
              <UsersFour weight="duotone" size={28} /> {project.title}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="share-modal-close-btn"
            className="border border-[--ink] p-2 hover:bg-[--ink] hover:text-white transition-colors"
            aria-label="Close"
          >
            <X weight="bold" size={14} />
          </button>
        </div>
        <p className="text-xs text-[--muted] mt-1 mb-5 leading-relaxed">
          Enter the email of an existing Flowscribe user. They'll see this project in their
          <strong> Shared with me </strong> list and can view/download the SRT (read-only).
        </p>

        <form onSubmit={submit} className="flex gap-2">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="user@example.com"
            data-testid="share-email-input"
            className="flex-1 border border-[--ink] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[--accent]"
          />
          <button
            type="submit"
            disabled={busy || !email.trim()}
            data-testid="share-add-btn"
            className="bg-[--accent] text-white px-5 py-3 text-sm font-bold hover:bg-[--accent-hi] transition-colors disabled:opacity-50"
          >
            {busy ? "Sharing…" : "Share"}
          </button>
        </form>

        <div className="mt-5">
          <div className="overline text-[--muted] mb-2">CURRENT MEMBERS</div>
          {!loaded ? (
            <div className="text-xs text-[--muted] font-mono py-3">Loading…</div>
          ) : shared.length === 0 ? (
            <div className="text-xs text-[--muted] font-mono py-3" data-testid="share-empty-state">
              Not shared with anyone yet.
            </div>
          ) : (
            <ul className="divide-y divide-[--line] border border-[--line]" data-testid="shared-users-list">
              {shared.map((u) => (
                <li key={u.user_id} className="flex items-center justify-between p-3" data-testid="shared-user-row">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">{u.name || u.email}</div>
                    <div className="text-[11px] font-mono text-[--muted] truncate">{u.email}</div>
                  </div>
                  <button
                    onClick={() => remove(u.user_id)}
                    data-testid="share-remove-btn"
                    className="border border-[--ink] px-3 py-2 text-xs font-bold hover:bg-[--accent] hover:text-white hover:border-[--accent] transition-colors"
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex justify-end pt-6">
          <button
            onClick={onClose}
            data-testid="share-modal-done-btn"
            className="bg-[--ink] text-white px-5 py-3 text-sm font-bold hover:bg-[--accent] transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
